import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import api from '../../services/api';
import ReplaceCardDialog from './ReplaceCardDialog';
import { getLinkedAccountIds } from '../../utils/categoryAccountUtils';
import { v4 as uuidv4 } from 'uuid';
import ScrollableFilters from '../Rules/components/ScrollableFilters';
import { useScrollableFilters } from '../Rules/hooks/useScrollableFilters';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Divider,
  Stack,
  Tab,
  Tabs,
  CircularProgress
} from '@mui/material';
import SectionHeader from '../common/SectionHeader';
import ListSection from '../common/ListSection';
import ToolbarButton from '../common/ToolbarButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add as AddIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InfoBanner from '../common/InfoBanner';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DragPreviewLayer from '../common/DragPreviewLayer';
import AppDialog, { DialogField } from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import AccountCard from './AccountCard';
import useBudgetData from '../Budget/useBudgetData';
import { forecastAccountBalanceSeries } from '../../utils/analysisForecast';
import {
  RANGE_OPTIONS,
  FORECAST_OPTIONS,
  monthsForRange,
  monthsForForecast,
  monthKeysForRange
} from '../../utils/entityTrendSeries';

// Same white-background filter fields as the Goals/Transactions toolbars.
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

// Gray pill Tabs recipe shared by the card display controls under the filters.
const pillTabsSx = {
  minHeight: 0,
  '& .MuiTab-root': { py: 0.75, px: 1.25, minHeight: 0, minWidth: 0, fontSize: '0.8125rem', borderRadius: 5, color: 'text.secondary', textTransform: 'none' },
  '& .Mui-selected': { color: 'primary.main', bgcolor: 'white' },
  '& .MuiTabs-indicator': { display: 'none' },
};

const AccountManager = () => {
  const { accounts, actions, budgets, categories, addAccount, updateAccount, deleteAccount, updateAccountBalance, transactions, fetchAccounts, fetchTransactions } = useData();
  const [replacementTarget, setReplacementTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    type: 'checking',
    balance: '',
    institution: '',
    color: '#4caf50',
    isActive: true,
    notes: '',
    linkedAccounts: [],
    linkedAccountMeta: {}
  });
  // Map of external (Akahu) account id -> friendly name, best-effort enriched
  // when the edit dialog opens.
  const [linkedAccountNames, setLinkedAccountNames] = useState({});
  // The user's connected bank (Akahu) accounts, for the link picker.
  const [akahuAccounts, setAkahuAccounts] = useState([]);
  const [akahuLoading, setAkahuLoading] = useState(false);
  const [akahuError, setAkahuError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [formError, setFormError] = useState('');
  const [orderedAccounts, setOrderedAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState(''); // '', 'checking', 'savings', 'credit', 'investment', 'loan'
  const [accountSort, setAccountSort] = useState(''); // '' = custom drag order, 'name' | 'type' | 'balance'
  const accountFiltersScroll = useScrollableFilters();

  // Chart window for every card, switched from the History pill next to the
  // Forecast toggle and remembered per page.
  const [range, setRange] = useState(() => {
    try {
      const saved = localStorage.getItem('accountsTrendRange');
      return RANGE_OPTIONS.some((o) => o.value === saved) ? saved : '6M';
    } catch {
      return '6M';
    }
  });
  const changeRange = (value) => {
    setRange(value);
    try {
      localStorage.setItem('accountsTrendRange', value);
    } catch {
      // persistence is best-effort
    }
  };
  const monthKeys = useMemo(() => monthKeysForRange(monthsForRange(range)), [range]);

  // Forecast horizon for the dashed projection on every card.
  const [forecast, setForecast] = useState(() => {
    try {
      const saved = localStorage.getItem('accountsForecastHorizon');
      return FORECAST_OPTIONS.some((o) => o.value === saved) ? saved : 'off';
    } catch {
      return 'off';
    }
  });
  const changeForecast = (value) => {
    setForecast(value);
    try {
      localStorage.setItem('accountsForecastHorizon', value);
    } catch {
      // persistence is best-effort
    }
  };
  const forecastMonths = monthsForForecast(forecast);

  // One shared run of the Analysis page's forecast math (BudgetView's
  // "Account Balances" table), so a card's dashed month-end forecast always
  // equals the same month's cell on the Analysis page.
  const budgetData = useBudgetData(transactions, categories);
  const forecastSeries = useMemo(
    () =>
      forecastMonths > 0
        ? forecastAccountBalanceSeries({ accounts, budgets, categories, actions, budgetData, transactions, monthsAhead: forecastMonths })
        : null,
    [accounts, budgets, categories, actions, budgetData, transactions, forecastMonths]
  );

  // Calculate last balance for each account from transactions
  const accountBalances = useMemo(() => {
    const balances = {};
    
    // Get latest transaction for each account
    accounts.forEach(account => {
      const accountTransactions = transactions.filter(t => t.account === account.id || t.account === account.name);
      
      if (accountTransactions.length > 0) {
        // Sort by date, most recent first
        const sortedTransactions = [...accountTransactions].sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        
        // Get the most recent transaction with a balance
        const latestTransaction = sortedTransactions.find(t => 
          t.balance !== undefined && t.balance !== null
        );
        
        if (latestTransaction) {
          balances[account.id] = {
            balance: latestTransaction.balance,
            date: latestTransaction.date
          };
        }
      }
    });
    
    return balances;
  }, [accounts, transactions]);

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      type: 'checking',
      balance: '',
      institution: '',
      color: '#4caf50',
      isActive: true,
      notes: '',
      linkedAccounts: [],
      linkedAccountMeta: {}
    });
    setFormError('');
    setLinkedAccountNames({});
    setAkahuAccounts([]);
    setAkahuError('');
  };

  // Fetch the user's connected bank (Akahu) accounts so the dialog can both show
  // friendly names ("Everyday — BNZ" rather than a raw "acc_...") and offer them
  // as link options. Degrades gracefully when Akahu isn't connected.
  const fetchAkahuAccounts = async () => {
    setAkahuLoading(true);
    setAkahuError('');
    try {
      const res = await api.get('/akahu/accounts');
      const items = res.data?.items || [];
      const map = {};
      items.forEach((acc) => {
        const id = acc._id || acc.id;
        if (!id) return;
        const bank = acc.connection?.name || acc.institution || '';
        map[id] = bank ? `${acc.name} · ${bank}` : acc.name;
      });
      setAkahuAccounts(items);
      setLinkedAccountNames(map);
      // Backfill stored names for links we could resolve live, so they persist
      // on the next Save and remain readable even if the bank later disconnects.
      setFormData((prev) => {
        const links = Array.isArray(prev.linkedAccounts) ? prev.linkedAccounts : [];
        const resolved = {};
        links.forEach((id) => { if (map[id]) resolved[id] = map[id]; });
        return { ...prev, linkedAccountMeta: { ...prev.linkedAccountMeta, ...resolved } };
      });
    } catch (err) {
      // No Akahu connection / tokens — ids will be shown as-is and the picker
      // explains that connecting a bank is needed to add links.
      setAkahuError('Connect a bank in Settings to add links.');
      console.debug('Could not load bank accounts:', err?.message);
    } finally {
      setAkahuLoading(false);
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      const linkedAccounts = getLinkedAccountIds(account);
      setFormData({
        id: account.id,
        name: account.name,
        type: account.type || 'checking',
        balance: account.balance || '',
        institution: account.institution || '',
        color: account.color || '#4caf50',
        isActive: account.isActive === undefined ? true : account.isActive,
        notes: account.notes || '',
        linkedAccounts,
        // Carry forward any stored display names (object, or Mongoose Map -> object).
        linkedAccountMeta: { ...(account.linkedAccountMeta || {}) }
      });
      setLinkedAccountNames({});
      setAkahuAccounts([]);
      setAkahuError('');
      fetchAkahuAccounts();
      setEditMode(true);
    } else {
      resetForm();
      setEditMode(false);
    }
    setDialogOpen(true);
  };

  // Toggle a bank (Akahu) account link on the account being edited, recording its
  // display name when linking so the chip stays readable offline. Persisted on Save.
  const handleToggleLink = (externalId, name) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.linkedAccounts) ? prev.linkedAccounts : [];
      const isLinked = current.includes(externalId);
      const next = isLinked
        ? current.filter((id) => id !== externalId)
        : [...current, externalId];
      const meta = { ...prev.linkedAccountMeta };
      if (isLinked) {
        delete meta[externalId];
      } else if (name) {
        meta[externalId] = name;
      }
      return { ...prev, linkedAccounts: next, linkedAccountMeta: meta };
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setFormError('Account name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editMode) {
        // Update existing account
        await updateAccount(formData.id, formData);
        
        // Update account balance if changed
        if (formData.balance !== '') {
          await updateAccountBalance(formData.id, parseFloat(formData.balance));
        }
      } else {
        // Create new account with a UUID
        const newAccount = {
          ...formData,
          id: uuidv4(),
          balance: formData.balance ? parseFloat(formData.balance) : 0
        };
        await addAccount(newAccount);
      }
      
      handleCloseDialog();
    } catch (err) {
      setError('Error saving account. Please try again.');
      console.error('Error saving account:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  // Ask Akahu to re-pull this account's bank data. The new data then arrives
  // via webhooks and is imported automatically — nothing else to do here.
  const handleBankRefresh = async (account) => {
    const linkedIds = getLinkedAccountIds(account);
    if (linkedIds.length === 0) return;
    setError(null);
    try {
      await Promise.all(linkedIds.map(id => api.post(`/akahu/refresh/${id}`)));
      setNotice(`Refresh requested for ${account.name}. New transactions will import automatically.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request a bank refresh.');
    }
  };

  const confirmDelete = async () => {
    if (!accountToDelete) {
      setDeleteDialogOpen(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteAccount(accountToDelete.id);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch (err) {
      setError('Error deleting account. Please try again.');
      console.error('Error deleting account:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize ordered accounts similar to categories ordering
  useEffect(() => {
    const sorted = [...accounts].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
    setOrderedAccounts(sorted);
  }, [accounts]);

  // Reordering with debounce save (persist order via updateAccount)
  const moveCard = (dragIndex, hoverIndex) => {
    const dragAccount = orderedAccounts[dragIndex];
    const newOrderedAccounts = [...orderedAccounts];
    newOrderedAccounts.splice(dragIndex, 1);
    newOrderedAccounts.splice(hoverIndex, 0, dragAccount);
    setOrderedAccounts(newOrderedAccounts);
    if (saveTimeout) clearTimeout(saveTimeout);
    const timeoutId = setTimeout(() => {
      newOrderedAccounts.forEach((account, index) => {
        if (account.order !== index) {
          updateAccount(account.id, { ...account, order: index });
        }
      });
    }, 500);
    setSaveTimeout(timeoutId);
  };

  // Link picker derived state: external ids we have a bank account for, and any
  // current links we couldn't resolve (still removable as chips).
  const availableExternalIds = akahuAccounts.map((a) => a._id || a.id).filter(Boolean);
  const currentLinks = formData.linkedAccounts || [];
  const staleLinks = currentLinks.filter((id) => !availableExternalIds.includes(id));

  // Best label for a linked id: live Akahu name, then the stored name, then a
  // shortened id so we never surface the raw "acc_..." in full.
  const labelForLink = (id) =>
    linkedAccountNames[id] || formData.linkedAccountMeta?.[id] || `Bank account · …${id.slice(-8)}`;

  // A bank account linked to more than one local account is ambiguous on import
  // (transactions match the first account found). Surface that as a warning.
  const linkConflicts = currentLinks
    .map((extId) => {
      const others = accounts.filter(
        (a) => a.id !== formData.id && getLinkedAccountIds(a).includes(extId)
      );
      return others.length ? { extId, others } : null;
    })
    .filter(Boolean);

  // Display-only sorting: the saved drag order stays the default (it drives
  // selectors & reports); picking a sort just reorders the visible grid.
  const visibleAccounts = orderedAccounts.filter(account => {
    const term = searchTerm.trim().toLowerCase();
    if (term &&
        !(account.name || '').toLowerCase().includes(term) &&
        !(account.institution || '').toLowerCase().includes(term)) {
      return false;
    }
    return !accountTypeFilter || account.type === accountTypeFilter;
  });
  const sortedAccounts = accountSort
    ? [...visibleAccounts].sort((a, b) => {
        if (accountSort === 'balance') {
          const av = accountBalances[a.id]?.balance ?? (Number(a.balance) || 0);
          const bv = accountBalances[b.id]?.balance ?? (Number(b.balance) || 0);
          return bv - av; // largest first
        }
        if (accountSort === 'type') {
          const cmp = (a.type || '').localeCompare(b.type || '');
          return cmp !== 0 ? cmp : (a.name || '').localeCompare(b.name || '');
        }
        return (a.name || '').localeCompare(b.name || '');
      })
    : visibleAccounts;

  return (
    <DndProvider backend={HTML5Backend}>
      <DragPreviewLayer />
      <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <SectionHeader
          title="Accounts"
          icon={<AccountBalanceIcon fontSize="large" />}
        />

      {/* Overflow guard sits below the header so it can't clip the
          full-bleed negative margins */}
      <Box sx={{ overflow: 'hidden' }}>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {notice && (
          <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: 3 }}>
            {notice}
          </Alert>
        )}

        <ListSection
          actions={[
            <ToolbarButton key="add" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add
            </ToolbarButton>,
            // Delete only appears once cards are ticked (via their
            // hover checkboxes), matching the Budgets page.
            ...(selectedAccountIds.length
              ? [
                  <ToolbarButton
                    key="bulkDelete"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBulkDeleteDialogOpen(true)}
                  >
                    Delete ({selectedAccountIds.length})
                  </ToolbarButton>
                ]
              : [])
          ]}
          filters={
            <ScrollableFilters
              scrollRef={accountFiltersScroll.scrollRef}
              showLeftScroll={accountFiltersScroll.showLeftScroll}
              showRightScroll={accountFiltersScroll.showRightScroll}
              onScrollLeft={accountFiltersScroll.scrollLeft}
              onScrollRight={accountFiltersScroll.scrollRight}
            >
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200, ...filterFieldSx }}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  style: { fontSize: '0.875rem' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                <InputLabel id="account-filter-type-label" shrink>Type</InputLabel>
                <Select
                  labelId="account-filter-type-label"
                  value={accountTypeFilter}
                  label="Type"
                  onChange={(e) => setAccountTypeFilter(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All ({accounts.length})</MenuItem>
                  <MenuItem value="checking" sx={{ fontSize: '0.875rem' }}>Checking ({accounts.filter(a => a.type === 'checking').length})</MenuItem>
                  <MenuItem value="savings" sx={{ fontSize: '0.875rem' }}>Savings ({accounts.filter(a => a.type === 'savings').length})</MenuItem>
                  <MenuItem value="credit" sx={{ fontSize: '0.875rem' }}>Credit ({accounts.filter(a => a.type === 'credit').length})</MenuItem>
                  <MenuItem value="investment" sx={{ fontSize: '0.875rem' }}>Investment ({accounts.filter(a => a.type === 'investment').length})</MenuItem>
                  <MenuItem value="loan" sx={{ fontSize: '0.875rem' }}>Loan ({accounts.filter(a => a.type === 'loan').length})</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170, ...filterFieldSx }}>
                <InputLabel id="account-sort-label" shrink>Sort</InputLabel>
                <Select
                  labelId="account-sort-label"
                  value={accountSort}
                  label="Sort"
                  onChange={(e) => setAccountSort(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Custom order</MenuItem>
                  <MenuItem value="name" sx={{ fontSize: '0.875rem' }}>Name</MenuItem>
                  <MenuItem value="type" sx={{ fontSize: '0.875rem' }}>Type</MenuItem>
                  <MenuItem value="balance" sx={{ fontSize: '0.875rem' }}>Balance</MenuItem>
                </Select>
              </FormControl>
            </ScrollableFilters>
          }
        >
          {/* Card display controls — the history window every card charts,
              and the Forecast horizon that extends every card's line with the
              dashed projectBalances continuation. Sit under the filter pills. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                History
              </Typography>
              <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5 }}>
                <Tabs
                  value={RANGE_OPTIONS.findIndex((o) => o.value === range)}
                  onChange={(e, idx) => changeRange(RANGE_OPTIONS[idx].value)}
                  sx={pillTabsSx}
                >
                  {RANGE_OPTIONS.map((o) => (
                    <Tab key={o.value} label={o.label} />
                  ))}
                </Tabs>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Forecast
              </Typography>
              <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5 }}>
                <Tabs
                  value={FORECAST_OPTIONS.findIndex((o) => o.value === forecast)}
                  onChange={(e, idx) => changeForecast(FORECAST_OPTIONS[idx].value)}
                  sx={pillTabsSx}
                >
                  {FORECAST_OPTIONS.map((o) => (
                    <Tab key={o.value} label={o.label} />
                  ))}
                </Tabs>
              </Box>
            </Box>
          </Box>
          {orderedAccounts.length > 0 && (
            <InfoBanner sx={{ mt: 2 }}>
              {accountSort
                ? 'Sorting is display-only. Switch Sort back to "Custom order" to drag and drop accounts into the order used by selectors & reports.'
                : 'Drag and drop to reorder accounts. This order controls how accounts appear in selectors & reports.'}
            </InfoBanner>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 1.75,
              pt: 2,
              alignItems: 'start',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(auto-fill, minmax(255px, 1fr))',
                md: 'repeat(auto-fill, minmax(290px, 1fr))'
              }
            }}
          >
            {sortedAccounts.map((account, index) => {
              const lastUpdatedRaw = (accountBalances[account.id] && accountBalances[account.id].date) || account.lastUpdated;
              const lastUpdated = lastUpdatedRaw ? new Date(lastUpdatedRaw).toLocaleDateString() : '';
              return (
                <AccountCard
                  key={account.id}
                  account={account}
                  index={index}
                  monthKeys={monthKeys}
                  forecast={forecastSeries ? forecastSeries[account.id] || null : null}
                  transactions={transactions}
                  onEdit={() => handleOpenDialog(account)}
                  onDelete={handleDeleteClick}
                  onBankRefresh={handleBankRefresh}
                  moveCard={accountSort ? () => {} : moveCard}
                  selectable
                  selected={selectedAccountIds.includes(account.id)}
                  onToggleSelect={(id) => setSelectedAccountIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                  lastUpdated={lastUpdated}
                />
              );
            })}
            {sortedAccounts.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No accounts found
              </Typography>
            )}
          </Box>
        </ListSection>

  {/* Add/Edit Account Dialog (mirrors Categories) */}
        <AppDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editMode ? 'Edit Account' : 'Add Account'}
          subtitle="Track a bank account, card, or wallet"
          primaryLabel={loading ? 'Saving…' : (editMode ? 'Save' : 'Add')}
          onPrimary={handleSubmit}
          primaryDisabled={loading}
        >
          {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
          <DialogField label="Name" required>
            <TextField
              fullWidth
              value={formData.name}
              name="name"
              onChange={handleInputChange}
              size="small"
              placeholder="e.g. Daily Expenses"
              autoFocus
            />
          </DialogField>
          {/* Type + Institution share a row — "what kind, at which bank". */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <DialogField label="Type" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                select
                value={formData.type}
                name="type"
                onChange={handleInputChange}
                size="small"
              >
                <MenuItem value="checking">Checking</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
                <MenuItem value="credit">Credit Card</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
                <MenuItem value="loan">Loan</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </DialogField>
            <DialogField label="Institution" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                value={formData.institution}
                name="institution"
                onChange={handleInputChange}
                size="small"
                placeholder="e.g. BNZ"
              />
            </DialogField>
          </Box>
          {/* Current balance + Color share a row. */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <DialogField label="Current balance" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                value={formData.balance}
                name="balance"
                type="number"
                onChange={handleInputChange}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </DialogField>
            <DialogField label="Color" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                value={formData.color}
                name="color"
                type="color"
                onChange={handleInputChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: formData.color }} />
                    </InputAdornment>
                  )
                }}
              />
            </DialogField>
          </Box>
          <DialogField label="Notes">
            <TextField
              fullWidth
              value={formData.notes}
              name="notes"
              onChange={handleInputChange}
              size="small"
              multiline
              rows={3}
              placeholder="Add any additional notes..."
            />
          </DialogField>
          {editMode && (
            <DialogField label="Bank links">
              <Stack spacing={1}>
                <Button variant="outlined" onClick={() => setReplacementTarget(formData.id)}>Replace a card / combine history</Button>
                <Alert
                  icon={<InfoOutlined fontSize="small" />}
                  severity="info"
                  variant="outlined"
                  sx={{ borderRadius: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}
                >
                  Connect this account to your real bank account(s) via Akahu. When you
                  import, transactions from a linked bank account are automatically filed
                  against this account, so balances and history stay in one place. Tick a
                  bank account below to link it, or untick to unlink.
                </Alert>

                {akahuLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading your bank accounts…
                    </Typography>
                  </Box>
                )}

                {/* Connected bank accounts — toggle to link/unlink */}
                {!akahuLoading && akahuAccounts.length > 0 && (
                  <Stack spacing={0}>
                    {akahuAccounts.map((acc) => {
                      const extId = acc._id || acc.id;
                      const bank = acc.connection?.name || acc.institution || '';
                      const label = bank ? `${acc.name} · ${bank}` : acc.name;
                      return (
                        <FormControlLabel
                          key={extId}
                          control={
                            <Checkbox
                              size="small"
                              checked={currentLinks.includes(extId)}
                              onChange={() => handleToggleLink(extId, label)}
                            />
                          }
                          label={<Typography variant="body2">{label}</Typography>}
                        />
                      );
                    })}
                  </Stack>
                )}

                {/* Links we couldn't resolve to a connected account — still removable */}
                {staleLinks.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {staleLinks.map((externalId) => (
                      <Tooltip key={externalId} title={`${externalId} is not currently connected. Remove?`}>
                        <Chip
                          icon={<AccountBalanceWalletIcon />}
                          label={`${labelForLink(externalId)} (not connected)`}
                          size="small"
                          variant="outlined"
                          color="default"
                          onDelete={() => handleToggleLink(externalId)}
                          sx={{ maxWidth: '100%' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                )}

                {!akahuLoading && akahuError && (
                  <Typography variant="caption" color="text.secondary">
                    {akahuError}
                  </Typography>
                )}

                {!akahuLoading && akahuAccounts.length === 0 && !akahuError && currentLinks.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No bank accounts connected.
                  </Typography>
                )}

                {linkConflicts.length > 0 && (
                  <Alert severity="warning" sx={{ py: 0, fontSize: '0.8125rem' }}>
                    {linkConflicts.map(({ extId, others }) => (
                      <Box key={extId}>
                        <strong>{labelForLink(extId)}</strong> is also linked to{' '}
                        {others.map((o) => o.name).join(', ')}. Imported transactions will be
                        filed against only one account.
                      </Box>
                    ))}
                  </Alert>
                )}
              </Stack>
            </DialogField>
          )}
          <Divider sx={{ mt: 2 }} />
          <DialogField label="Status" sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={formData.isActive} onChange={handleInputChange} name="isActive" />}
              label={<Typography variant="body2">Active</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Inactive accounts keep all their transactions but are hidden from
              active-account views.
            </Typography>
          </DialogField>
        </AppDialog>

        {/* Single Delete Confirmation Dialog */}
        <ReplaceCardDialog targetId={replacementTarget} onClose={() => setReplacementTarget(null)} onComplete={async () => {
          await Promise.all([fetchAccounts(), fetchTransactions({ silent: true })]);
          setReplacementTarget(null);
          handleCloseDialog();
          setNotice('Card history combined. The replacement feed will supply the current balance after sync.');
        }} />
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Account"
          message={`Are you sure you want to delete the account "${accountToDelete?.name}"? This will affect all transactions associated with this account.`}
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          loading={loading}
        />
        {/* Bulk Delete Confirmation Dialog */}
        <ConfirmDialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
          title={`Delete ${selectedAccountIds.length} account${selectedAccountIds.length === 1 ? '' : 's'}?`}
          message="This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={() => {
            selectedAccountIds.forEach(id => deleteAccount(id));
            setSelectedAccountIds([]);
            setBulkDeleteDialogOpen(false);
          }}
        />
      </Box>
      </Box>
    </DndProvider>
  );
};

export default AccountManager;
